import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { displayName } from "../lib/anagConfig";
import { Loader2, Search } from "lucide-react";

// Selezione ispettorati in cascata dalla compagnia: mostra SOLO gli ispettorati appartenenti
// alle compagnie selezionate. Ogni voce è etichettata "Nome Ispettorato - Nome Compagnia".
// Se nessuna compagnia è selezionata, l'elenco resta vuoto. Il valore è una lista di nomi ispettorato.
export default function IspettoratiCascadeSelect({ compagnie = [], value = [], onChange, testId = "ispettorati-cascade" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/anagrafiche/compagnie")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const norm = (s) => (s || "").trim().toLowerCase();
  const selectedComp = useMemo(() => new Set((compagnie || []).map(norm)), [compagnie]);

  // Opzioni: solo ispettorati delle compagnie selezionate, con etichetta "Ispettorato - Compagnia".
  const options = useMemo(() => {
    const out = [];
    (items || []).forEach((c) => {
      const compName = displayName(c) || c.denominazione || c.ragione_sociale || "";
      if (!selectedComp.has(norm(compName))) return;
      (c.ispettorati || []).forEach((i) => {
        const name = (typeof i === "string" ? i : (i?.denominazione || i?.nome || "")).trim();
        if (name) out.push({ name, comp: compName, label: `${name} - ${compName}` });
      });
    });
    return out.sort((a, b) => a.label.localeCompare(b.label, "it", { sensitivity: "base" }));
  }, [items, selectedComp]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.label.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const toggle = (name) => {
    const set = new Set(value || []);
    set.has(name) ? set.delete(name) : set.add(name);
    onChange([...set]);
  };

  if (loading) return <div className="mt-1.5 py-2"><Loader2 className="h-4 w-4 animate-spin text-amber-500" /></div>;

  return (
    <div className="mt-1.5" data-testid={testId}>
      <div className="relative mb-1.5">
        <Search className="h-3.5 w-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ispettorato…" data-testid={`${testId}-search`}
          className="h-8 w-full rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm pl-7 pr-2 focus:outline-none focus:ring-1 focus:ring-amber-500/40" />
      </div>
      <div className="max-h-40 overflow-y-auto rounded-md border border-slate-700 bg-slate-900/60 p-2 space-y-1">
        {selectedComp.size === 0 ? (
          <div className="text-xs text-slate-500 px-1 py-1">Seleziona prima una compagnia per vedere i relativi ispettorati.</div>
        ) : options.length === 0 ? (
          <div className="text-xs text-slate-500 px-1 py-1">Nessun ispettorato per le compagnie selezionate.</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-slate-500 px-1 py-1">Nessun risultato per “{q}”.</div>
        ) : filtered.map((o) => (
          <label key={`${o.comp}::${o.name}`} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer px-1 py-0.5 rounded hover:bg-slate-800/60">
            <input type="checkbox" checked={(value || []).includes(o.name)} onChange={() => toggle(o.name)} data-testid={`${testId}-opt`} className="accent-amber-500" />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}
